<template>
  <div>
    <OrderHeaderRow
      :columns="innerLayout"
      :sort-by.sync="sortBy"
      :reserve-queue="reserveQueue"
      :class="groupBy == 'none' ? 'group-header' : 'inner-header'"
    />
    <OrderItemRow
      v-for="(o, i) in visibleOrders"
      :key="o.id"
      :columns="innerLayout"
      :order="o"
      :last="groupBy === 'none' && i === (orders.length - 1)"
      :payer="o.payer"
      :checked="!!checked[o.id]"
      :reserve-queue="reserveQueue"
      :selected="editedId === o.id"
      @toggle="toggle(o, !checked[o.id])"
      @select="$emit('select', o)"
    />
    <InfinityScrollCursor
      loop-check
      :margin="10"
      @request="mainCursor += 30"
    />
    <div
      v-if="pending || !isBottom"
      class="mt-4"
      style="max-width: 90vw"
    >
      <Loader />
    </div>

    <div
      v-else-if="orders.length === 0"
      class="text-center py-3 text-secondary"
    >
      <div style="font-size: 1rem">
        <span class="fa-stack fa-2x">
          <i class="far fa-box" />
          <i class="far fa-slash fa-stack-1x" />
        </span>
      </div>
      {{ $t('shop.noOrders') }}
    </div>
  </div>
</template>

<script>
import InfinityScrollCursor from '@/components/InfinityScrollCursor';
import moment from 'moment';
import { mapGetters } from 'vuex';
import OrderHeaderRow from './OrderHeaderRow';
import OrderItemRow from './OrderItemRow';

export default {
  props: {
    payers: {
      type: Array,
      default: () => [],
    },
    productSets: {
      type: Object,
      default: () => ({}),
    },
    layout: {
      type: Array,
      default: () => [],
    },
    dates: {
      type: Object,
      default: () => ({}),
    },
    checked: Object,
    orders: Array,
    groupBy: String,
    editedId: String,
    pending: Boolean,
    reserveQueue: Boolean,
  },
  data: () => ({
    sortBy: 'payer',
    mainCursor: 30,
    haveOrders: false,
  }),
  components: {
    InfinityScrollCursor,
    OrderItemRow,
    OrderHeaderRow,
  },
  computed: {
    ...mapGetters('shop', ['catalogDetails']),
    checkedOrders() {
      return this.ordersWithData.filter((o) => this.checked[o.id]);
    },
    visibleOrders() {
      return this.ordersWithData.slice(0, this.mainCursor);
    },
    isBottom() {
      return this.mainCursor >= this.ordersWithData.length;
    },
    innerLayout() {
      if (this.groupBy === 'none') return this.layout;
      if (this.groupBy === 'payers') {
        return this.layout
          .filter((x) => x.key !== 'code'
            && x.key !== 'name'
            && x.type !== 'property'
            && x.type !== 'group');
      }
      if (this.groupBy === 'productSets') {
        return this.layout
          .filter((x) => x.key !== 'productSetName');
      }
      if (this.groupBy === 'day') {
        return this.layout
          .filter((x) => x.key !== 'day');
      }

      return this.layout;
    },
    checkedCount() {
      return this.checkedOrders.reduce((acc, curr) => acc + curr.count, 0);
    },
    payersDictionary() {
      return Object.freeze(this.payers.reduce((acc, curr) => {
        acc[curr.id] = curr;
        return acc;
      }, {}));
    },
    statusDictionary() {
      return Object.freeze(this.catalogDetails.orderStatuses.reduce((acc, curr) => {
        acc[curr.id] = curr;
        return acc;
      }, {}));
    },
    ordersWithData() {
      return Object.freeze(this.orders
        .map((o) => ({
          ...o,
          payer: this.payersDictionary[o.payerId],
          status: this.statusDictionary[o.orderStatusId],
          set: this.productSets[o.productSetId] || {},
          listing: this.productSets[o.productSetId] || {},
          formattedDay: moment(o.day, 'YYYY-MM-DD').format('DD/MM'),
          formattedMonth: moment(o.day, 'YYYY-MM-DD').format('MM/YYYY'),
        }))
        .sort((a, b) => {
          if (this.reserveQueue) {
            if (a.position === b.position) {
              return moment(a.created).unix() - moment(b.created).unix();
            }

            return a.position - b.position;
          }

          if (this.sortBy === 'created-up') {
            return moment(b.created).unix() - moment(a.created).unix();
          }
          if (this.sortBy === 'created-down') {
            return moment(b.created).unix() - moment(a.created).unix();
          }
          if (this.sortBy === 'day-up') {
            return moment(b.day).unix() - moment(a.day).unix();
          }
          if (this.sortBy === 'day-down') {
            return moment(b.day).unix() - moment(a.day).unix();
          }
          return a.payer?.name.localeCompare(b.payer?.name);
        }));
    },
  },
  watch: {
    groupBy() {
      this.mainCursor = 30;
    },
  },
  methods: {
    toggle(o, value) {
      if (value) {
        this.$emit('checked', o);
      } else {
        this.$emit('unchecked', o);
      }
    },
  },
};
</script>

<style lang="scss" scoped>

.payer-row-content {
  padding: 5px 10px 5px 5px;
}

.column-divider {
  border-left: 1px solid rgba(100, 100, 100, 0.1);
}

.money-font {
  font-size: 0.9rem;
}

.inner-table {
  margin-top: 10px;
  margin-bottom: 10px;
  margin-left: 27px;
  border: 1px solid #dddddd;
  border-radius: 10px;
  width: min-content;
}

.inner-header {
  background-color: #f6f6f6;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}

.group-header {
  background-color: #f6f6f6;
  width: min-content;
  min-width: 100%;
}

.collapse-icon {
  width: 27px;
  text-align: center;
  margin-left: 5px;
  line-height: 30px;
  display: flex;
  align-items: center;

  i {
    background-color: #f6f6f6;
    padding: 5px;
    border-radius: 50%;
    width: 22px;
    min-width: 22px;
    height: 22px;
    line-height: 12px;
    font-size: 15px;
    color: #666;
  }
}

.main-column {
  width: 250px;
  max-width: 250px;
  min-width: 250px;
  font-weight: 0.9rem;
}

.header-wrapper {
  min-width: min-content;
}

.group-row {
  &:deep(.set-row) {
    border-top: none;
    cursor: default;
  }
  &:deep(.set-row-hover:hover) {
    background-color: initial;
  }
}

.orders-count {
  width: 120px;
  font-weight: 400;
  border-radius: 5px;
  text-align: right;
}

.orders-paid {
  color: $blue;
  text-align: right;
  width: 120px;
}

.orders-unpaid {
  color: $orange;
  text-align: right;
  width: 120px;
}
</style>
